import { create } from 'zustand'
import TencentCloudChat from '@tencentcloud/chat'
import { getTrtcChatInstance } from '@/hooks/useTrtcChat'

const APPROVAL_POLL_INTERVAL = 10_000 // 10s
let approvalPollTimer: ReturnType<typeof setInterval> | null = null

export interface TrtcChatStoreState {
  // SDK 状态
  isSDKReady: boolean
  isLoggedIn: boolean
  isLoggingIn: boolean

  // 未读数
  totalUnreadCount: number
  approvalUnreadCount: number

  // 会话和群组数据（由 TrtcChat 组件事件回调同步写入）
  conversations: any[]
  groups: any[]
  isConvListDone: boolean; // 是否拉取完会话了

  // 网络/踢出告警
  networkAlert: { type: string | null; message: string }

  // 内部：存储全局事件处理函数引用，用于 logout 时移除
  _globalHandlers: Record<string, (...args: any[]) => void> | null

  // 方法
  login: (userId: string, userSig: string) => Promise<void>
  logout: () => Promise<void>
  setSDKReady: (ready: boolean) => void
  setLoggedIn: (loggedIn: boolean) => void
  setTotalUnreadCount: (count: number) => void
  setConversations: (list: any[]) => void
  setGroups: (list: any[]) => void
  setIsConvListDone: (resp: boolean) => void
  setNetworkAlert: (alert: { type: string | null; message: string }) => void
  setApprovalUnreadCount: (count: number) => void
  startApprovalPolling: () => void
  stopApprovalPolling: () => void
  getSDKInstance: () => any
}

export const useTrtcChatStore = create<TrtcChatStoreState>()((set, get) => ({
  isSDKReady: false,
  isLoggedIn: false,
  isLoggingIn: false,
  isConvListDone: false,
  totalUnreadCount: 0,
  approvalUnreadCount: 0,
  conversations: [],
  groups: [],
  networkAlert: { type: null, message: '' },
  _globalHandlers: null,

  setSDKReady: (ready) => set({ isSDKReady: ready }),
  setLoggedIn: (loggedIn) => set({ isLoggedIn: loggedIn }),
  setTotalUnreadCount: (count) => set({ totalUnreadCount: count }),
  setConversations: (list) => set({ conversations: list }),
  setGroups: (list) => set({ groups: list }),
  setIsConvListDone: (resp) => set({isConvListDone: resp}),
  setNetworkAlert: (alert) => set({ networkAlert: alert }),
  setApprovalUnreadCount: (count) => set({ approvalUnreadCount: count }),

  startApprovalPolling: () => {
    // 避免重复启动
    if (approvalPollTimer) return
    const fetchUnread = async () => {
      const { groups, isLoggedIn } = get()
      const hasAdminGroup = groups.some(
        (g: any) =>
          g.selfInfo?.role &&
          g.selfInfo.role !== TencentCloudChat.TYPES.GRP_MBR_ROLE_MEMBER
      )
      if (!isLoggedIn || !hasAdminGroup) {
        set({ approvalUnreadCount: 0 })
        return
      }
      try {
        const { trtcApi } = await import('@/lib/trtcchat-api')
        const res = await trtcApi.group.getApprovalUnreadCount()
        if (res?.code === 200 && res?.data) {
          set({ approvalUnreadCount: res.data.unreadCount || 0 })
        }
      } catch {
        // ignore
      }
    }
    fetchUnread()
    approvalPollTimer = setInterval(fetchUnread, APPROVAL_POLL_INTERVAL)
  },

  stopApprovalPolling: () => {
    if (approvalPollTimer) {
      clearInterval(approvalPollTimer)
      approvalPollTimer = null
    }
    set({ approvalUnreadCount: 0 })
  },

  getSDKInstance: () => getTrtcChatInstance(),

  login: async (userId, userSig) => {
    const tc = getTrtcChatInstance()
    if (!tc) return
    const state = get()
    if (state.isLoggingIn || state.isLoggedIn) return

    set({ isLoggingIn: true })

    // 注册全局事件监听
    const onSDKReady = () => {
      console.log('[TrtcChatStore] sdk ready')
      set({ isSDKReady: true })
    }
    const onSDKNotReady = () => {
      console.log('[TrtcChatStore] sdk not ready')
      set({ isSDKReady: false })
    }
    const onKickedOut = (event: any) => {
      console.log('[TrtcChatStore] KICKED_OUT:', event.data.type)
      set({ isLoggedIn: false, isSDKReady: false })
      let message = 'message.account.kickedOut'
      if (event.data.type === TencentCloudChat.TYPES.KICKED_OUT_MULT_ACCOUNT) {
        message = 'message.account.kickedOutMultiAccount'
      } else if (event.data.type === TencentCloudChat.TYPES.KICKED_OUT_MULT_DEVICE) {
        message = 'message.account.kickedOutMultiDevice'
      } else if (event.data.type === TencentCloudChat.TYPES.KICKED_OUT_USERSIG_EXPIRED) {
        message = 'message.account.kickedOutExpired'
      }
      set({ networkAlert: { type: 'kicked', message } })
    }
    const onNetStateChange = (event: any) => {
      console.log('[TrtcChatStore] NET_STATE_CHANGE:', event.data.state)
      if (event.data.state === TencentCloudChat.TYPES.NET_STATE_DISCONNECTED) {
        set({ networkAlert: { type: 'disconnected', message: 'message.network.disconnected' } })
      } else if (event.data.state === TencentCloudChat.TYPES.NET_STATE_CONNECTED) {
        const prev = get().networkAlert
        if (prev.type === 'disconnected') {
          set({ networkAlert: { type: null, message: '' } })
        }
      }
    }
    const onTotalUnreadCountUpdated = (event: any) => {
      console.log('[TrtcChatStore] TOTAL_UNREAD:', event.data)
      set({ totalUnreadCount: event.data || 0 })
    }
    const onConversationListUpdated = (event: any) => {
      const conversationList = event.data
      console.log('[TrtcChatStore] CONVERSATION_LIST_UPDATED:', conversationList)
      set({ conversations: conversationList, isConvListDone: event.isSyncCompleted })
    }
    const onGroupListUpdated = (event: any) => {
      const groups = event.data
      console.log('[TrtcChatStore] GROUP_LIST_UPDATED:', groups)
      set({ groups })
      // groups 变化后重新检查是否需要轮询审批未读数
      get().startApprovalPolling()
    }

    tc.on(TencentCloudChat.EVENT.SDK_READY, onSDKReady)
    tc.on(TencentCloudChat.EVENT.SDK_NOT_READY, onSDKNotReady)
    tc.on(TencentCloudChat.EVENT.KICKED_OUT, onKickedOut)
    tc.on(TencentCloudChat.EVENT.NET_STATE_CHANGE, onNetStateChange)
    tc.on(TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED, onTotalUnreadCountUpdated)
    tc.on(TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED, onConversationListUpdated)
    tc.on(TencentCloudChat.EVENT.GROUP_LIST_UPDATED, onGroupListUpdated)

    set({
      _globalHandlers: {
        onSDKReady, onSDKNotReady, onKickedOut, onNetStateChange, onTotalUnreadCountUpdated, onConversationListUpdated, onGroupListUpdated
      }
    })

    try {
      await tc.login({ userID: userId, userSig })
      console.log('[TrtcChatStore] login success')
      set({ isLoggedIn: true })
    } catch (error: any) {
      console.error('[TrtcChatStore] login error:', error)
      if (error?.code === 70001) {
        set({ networkAlert: { type: 'kicked', message: 'message.account.kickedOutExpired' } })
      }
    } finally {
      set({ isLoggingIn: false })
    }
  },
  logout: async () => {
    get().stopApprovalPolling()
    const tc = getTrtcChatInstance()
    const handlers = get()._globalHandlers
    if (tc && handlers) {
      tc.off(TencentCloudChat.EVENT.SDK_READY, handlers.onSDKReady)
      tc.off(TencentCloudChat.EVENT.SDK_NOT_READY, handlers.onSDKNotReady)
      tc.off(TencentCloudChat.EVENT.KICKED_OUT, handlers.onKickedOut)
      tc.off(TencentCloudChat.EVENT.NET_STATE_CHANGE, handlers.onNetStateChange)
      tc.off(TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED, handlers.onTotalUnreadCountUpdated)
      tc.off(TencentCloudChat.EVENT.CONVERSATION_LIST_UPDATED, handlers.onConversationListUpdated)
      tc.off(TencentCloudChat.EVENT.GROUP_LIST_UPDATED, handlers.onGroupListUpdated)
    }
    try {
      if (tc) await tc.logout()
    } catch (e) {
      console.warn('[TrtcChatStore] logout error:', e)
    }
    set({
      isSDKReady: false,
      isLoggedIn: false,
      isLoggingIn: false,
      isConvListDone: false,
      totalUnreadCount: 0,
      approvalUnreadCount: 0,
      conversations: [],
      groups: [],
      networkAlert: { type: null, message: '' },
      _globalHandlers: null,
    })
  },
}))
